import { useRoutes, Navigate } from 'react-router-dom';

// project import
import LoginRoutes from './LoginRoutes';
import MainRoutes from './MainRoutes';
import SuperAdminRoutes from './SuperAdminRoutes';
import { useGiraf } from 'giraf/index';
import { useEffect } from 'react';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { lazy } from 'react';
import Loadable from 'components/Loadable';
import MinimalLayout from 'layout/MinimalLayout';

const LandingPage = Loadable(lazy(() => import('pages/landing/LandingPage')));

// Always-public routes (landing visible whether logged in or not)
const PublicRoutes = {
  path: '/',
  element: <MinimalLayout />,
  children: [{ path: 'landing', element: <LandingPage /> }]
};

// ==============================|| ROUTING RENDER ||============================== //

// Synchronously read cookies once so the first render already knows auth state
// (avoids a flash of the wrong route tree before useEffect fires)
function readAuthFromCookies() {
  const result = { authenticated: false, isSuperAdmin: false };
  try {
    const auth = Cookies.get('AuthToken');
    if (auth) {
      jwtDecode(auth.split('Bearer ')[1]); // throws if invalid
      result.authenticated = true;
    }
  } catch { /* expired / malformed */ }
  try {
    const adminAuth = Cookies.get('AdminToken');
    if (adminAuth) {
      const u = jwtDecode(adminAuth.split('Bearer ')[1]);
      if (u.is_super_admin) result.isSuperAdmin = true;
    }
  } catch { /* expired / malformed */ }
  return result;
}

export default function ThemeRoutes() {
  const { gHead, addGHead } = useGiraf();

  useEffect(() => {
    // Check for regular user token
    const auth = Cookies.get('AuthToken');
    if (auth) {
      try {
        addGHead('AuthToken', auth);
        const token = auth.split('Bearer ')[1];
        const user = jwtDecode(token);
        addGHead('AuthUser', user);
        addGHead('Authenticated', true);
      } catch {
        // invalid / expired token — stay on public routes
      }
    }

    // Check for super admin token
    const adminAuth = Cookies.get('AdminToken');
    if (adminAuth) {
      try {
        const token = adminAuth.split('Bearer ')[1];
        const user = jwtDecode(token);
        if (user.is_super_admin) {
          addGHead('AdminToken', adminAuth);
          addGHead('AdminUser', user);
          addGHead('IsSuperAdmin', true);
        }
      } catch {
        // invalid / expired admin token
      }
    }
  }, []);

  // Use giraf state if already hydrated, otherwise fall back to the synchronous
  // cookie check so the very first render picks the right route tree.
  const initial = readAuthFromCookies();
  const isAdmin = gHead.IsSuperAdmin ?? initial.isSuperAdmin;
  const isAuth  = gHead.Authenticated ?? initial.authenticated;

  // Redirect catch-all for super admin: any non-/admin path → /admin/dashboard
  const AdminFallback = {
    path: '*',
    element: <Navigate to="/admin/dashboard" replace />
  };
  // Redirect catch-all for regular auth users: unknown paths → /
  const AuthFallback = {
    path: '*',
    element: <Navigate to="/" replace />
  };

  const activeRoutes = isAdmin
    ? [SuperAdminRoutes, AdminFallback]
    : isAuth
      ? [MainRoutes, PublicRoutes, AuthFallback]
      : [LoginRoutes];

  return useRoutes(activeRoutes);
}
